import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { validateLevel, goalAvailability } from '../lib/levelSchema';
import './LevelEditor.css';

const CANDY_COLORS = { 1: '#E74C3C', 2: '#3498DB', 3: '#2ECC71', 4: '#F1C40F', 5: '#9B59B6' };
const LAYER_KEYS = ['candiesLayer', 'blockersLayer', 'overlaysLayer', 'blockerHpLayer', 'overlayHpLayer', 'boosterColorsLayer'];

function cloneLevel(level) {
  return JSON.parse(JSON.stringify(level));
}

function Cell({ level, r, c, onPaint, onCycleHp }) {
  const piece = level.candiesLayer[r][c];
  const blocker = level.blockersLayer[r][c];
  const overlay = level.overlaysLayer[r][c];
  const bhp = level.blockerHpLayer[r][c];
  const ohp = level.overlayHpLayer[r][c];
  const color = level.boosterColorsLayer[r][c];

  if (piece === -1) {
    return (
      <button
        type="button"
        className="cell hole"
        onMouseDown={() => onPaint(r, c)}
        onMouseEnter={(e) => e.buttons === 1 && onPaint(r, c)}
      />
    );
  }

  const style = {};
  let shape = null;
  if (piece >= 1 && piece <= 5) {
    style.background = CANDY_COLORS[piece];
  } else if (piece >= 6 && piece <= 10) {
    style.background = CANDY_COLORS[color] || '#2C3E50';
    if (piece === 6) shape = <div className="stripe-h" />;
    else if (piece === 7) shape = <div className="stripe-v" />;
    else if (piece === 8) shape = <div className="wrapped-ring" />;
    else if (piece === 9) shape = <div className="bomb-star" />;
    else if (piece === 10) shape = <div className="fish-dot" />;
  }

  return (
    <button
      type="button"
      className="cell"
      style={style}
      onMouseDown={() => onPaint(r, c)}
      onMouseEnter={(e) => e.buttons === 1 && onPaint(r, c)}
      onContextMenu={(e) => {
        e.preventDefault();
        onCycleHp(r, c);
      }}
    >
      {shape && <div className="shape">{shape}</div>}
      {overlay === 1 && <div className="overlay-chain" />}
      {overlay === 2 && <div className="overlay-ice" />}
      {overlay > 0 && ohp > 1 && <div className="badge">hp{ohp}</div>}
      {blocker > 0 && <div className="blocker-mark" />}
      {blocker > 0 && <div className="badge">hp{bhp}</div>}
    </button>
  );
}

export default function LevelEditor({ initialLevel, onSave, onCancel }) {
  const [level, setLevel] = useState(() => cloneLevel(initialLevel));
  const [activeLayer, setActiveLayer] = useState('candy');
  const [candyBrush, setCandyBrush] = useState(1);
  const [blockerBrush, setBlockerBrush] = useState(1);
  const [overlayBrush, setOverlayBrush] = useState(1);
  const [boosterColor, setBoosterColor] = useState(1);
  const [blockerHpBrush, setBlockerHpBrush] = useState(1);
  const [overlayHpBrush, setOverlayHpBrush] = useState(1);
  const [saving, setSaving] = useState(false);
  const paintingRef = useRef(false);
  const [widthInput, setWidthInput] = useState(level.width);
  const [heightInput, setHeightInput] = useState(level.height);
  const [toastMsg, setToastMsg] = useState('');

  useEffect(() => {
    const up = () => (paintingRef.current = false);
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const flashTimer = useRef(null);
  const flash = useCallback((msg) => {
    setToastMsg(msg);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setToastMsg(''), 2500);
  }, []);

  const mutate = useCallback((fn) => {
    setLevel((prev) => {
      const next = { ...prev };
      fn(next);
      return next;
    });
  }, []);

  const paintCell = useCallback(
    (r, c) => {
      paintingRef.current = true;
      mutate((d) => {
        if (activeLayer === 'candy') {
          const v = candyBrush;
          if (v === -1) {
            d.candiesLayer[r][c] = -1;
            d.blockersLayer[r][c] = 0;
            d.blockerHpLayer[r][c] = 0;
            d.overlaysLayer[r][c] = 0;
            d.overlayHpLayer[r][c] = 0;
            d.boosterColorsLayer[r][c] = 0;
          } else {
            if (d.blockersLayer[r][c] > 0) {
              d.blockersLayer[r][c] = 0;
              d.blockerHpLayer[r][c] = 0;
            }
            d.candiesLayer[r][c] = v;
            const isColored = v >= 6 && (v <= 8 || v === 10);
            d.boosterColorsLayer[r][c] = isColored ? boosterColor : 0;
          }
        } else if (activeLayer === 'blocker') {
          if (d.candiesLayer[r][c] === -1) {
            flash('Không thể đặt crate trên ô hole.');
            return;
          }
          if (blockerBrush === 1) {
            d.candiesLayer[r][c] = 0;
            d.boosterColorsLayer[r][c] = 0;
            d.overlaysLayer[r][c] = 0;
            d.overlayHpLayer[r][c] = 0;
            d.blockersLayer[r][c] = 1;
            d.blockerHpLayer[r][c] = blockerHpBrush;
          } else {
            d.blockersLayer[r][c] = 0;
            d.blockerHpLayer[r][c] = 0;
          }
        } else if (activeLayer === 'overlay') {
          if (d.candiesLayer[r][c] === -1) {
            flash('Không thể đặt overlay trên ô hole.');
            return;
          }
          if (overlayBrush > 0) {
            if (d.blockersLayer[r][c] > 0) {
              d.blockersLayer[r][c] = 0;
              d.blockerHpLayer[r][c] = 0;
            }
            d.overlaysLayer[r][c] = overlayBrush;
            d.overlayHpLayer[r][c] = overlayHpBrush;
          } else {
            d.overlaysLayer[r][c] = 0;
            d.overlayHpLayer[r][c] = 0;
          }
        }
      });
    },
    [activeLayer, candyBrush, blockerBrush, overlayBrush, boosterColor, blockerHpBrush, overlayHpBrush, mutate, flash]
  );

  const cycleHp = useCallback(
    (r, c) => {
      mutate((d) => {
        if (activeLayer === 'blocker' && d.blockersLayer[r][c] > 0) {
          d.blockerHpLayer[r][c] = (d.blockerHpLayer[r][c] % 3) + 1;
        } else if (activeLayer === 'overlay' && d.overlaysLayer[r][c] > 0) {
          d.overlayHpLayer[r][c] = (d.overlayHpLayer[r][c] % 3) + 1;
        }
      });
    },
    [activeLayer, mutate]
  );

  const applyResize = () => {
    const w = Math.max(1, Math.min(12, parseInt(widthInput || '1', 10)));
    const h = Math.max(1, Math.min(12, parseInt(heightInput || '1', 10)));
    mutate((d) => {
      for (const key of LAYER_KEYS) {
        const old = d[key];
        d[key] = Array.from({ length: h }, (_, r) =>
          Array.from({ length: w }, (_, c) => {
            if (r < old.length && old[0] && c < old[0].length) return old[r][c];
            return key === 'candiesLayer' ? ((r + c) % 5) + 1 : 0;
          })
        );
      }
      d.width = w;
      d.height = h;
    });
    setWidthInput(w);
    setHeightInput(h);
  };

  const setField = (patch) => mutate((d) => Object.assign(d, patch));
  const setDropRate = (k, v) => mutate((d) => (d.dropRates = { ...d.dropRates, [k]: parseInt(v || '0', 10) }));
  const setStar = (i, v) => mutate((d) => {
    const next = [...d.starScores];
    next[i] = parseInt(v || '0', 10);
    d.starScores = next;
  });

  const addGoal = () => mutate((d) => (d.goals = [...d.goals, { target: 'candy', type: 1, count: 5 }]));
  const removeGoal = (i) => mutate((d) => (d.goals = d.goals.filter((_, gi) => gi !== i)));
  const updateGoal = (i, patch) =>
    mutate((d) => {
      const goals = d.goals.map((g, gi) => (gi === i ? { ...g, ...patch } : g));
      const maxType = { candy: 5, blocker: 1, overlay: 2 };
      const g = goals[i];
      if (g.type < 1 || g.type > maxType[g.target]) g.type = 1;
      d.goals = goals;
    });

  const errors = useMemo(() => validateLevel(level), [level]);

  const handleSave = async () => {
    if (errors.length) {
      flash(`Còn ${errors.length} lỗi, không thể lưu.`);
      return;
    }
    setSaving(true);
    try {
      await onSave(level);
    } finally {
      setSaving(false);
    }
  };

  const maxTypeFor = { candy: 5, blocker: 1, overlay: 2 };

  return (
    <div>
      <div className="editor-panel">
        <h2>Level info — #{level.levelId}</h2>
        <div className="editor-grid2">
          <label>Chapter
            <input type="number" min="1" value={level.chapter} onChange={(e) => setField({ chapter: parseInt(e.target.value || '1', 10) })} />
          </label>
          <label>Mode
            <select value={level.mode} onChange={(e) => setField({ mode: e.target.value })}>
              <option value="objectives">objectives</option>
              <option value="score">score</option>
              <option value="survival">survival</option>
            </select>
          </label>
          {level.mode === 'score' && (
            <label>Target score
              <input type="number" min="1" value={level.targetScore} onChange={(e) => setField({ targetScore: parseInt(e.target.value || '0', 10) })} />
            </label>
          )}
          <label>Moves
            <input type="number" min="1" value={level.moves} onChange={(e) => setField({ moves: parseInt(e.target.value || '1', 10) })} />
          </label>
          <label>Width
            <input type="number" min="1" max="12" value={widthInput} onChange={(e) => setWidthInput(e.target.value)} />
          </label>
          <label>Height
            <input type="number" min="1" max="12" value={heightInput} onChange={(e) => setHeightInput(e.target.value)} />
          </label>
          <button className="btn" type="button" onClick={applyResize}>Resize grid</button>
        </div>
        <div className="editor-grid2">
          <label>★1 score <input type="number" min="0" value={level.starScores[0]} onChange={(e) => setStar(0, e.target.value)} /></label>
          <label>★2 score <input type="number" min="0" value={level.starScores[1]} onChange={(e) => setStar(1, e.target.value)} /></label>
          <label>★3 score <input type="number" min="0" value={level.starScores[2]} onChange={(e) => setStar(2, e.target.value)} /></label>
        </div>
        <h3>Drop rates</h3>
        <div className="drop-rates">
          {['1', '2', '3', '4', '5'].map((k) => (
            <label key={k}>
              <span style={{ color: CANDY_COLORS[k] }}>●</span> Color {k}
              <input type="number" min="0" value={level.dropRates[k] ?? 0} onChange={(e) => setDropRate(k, e.target.value)} />
            </label>
          ))}
        </div>
      </div>

      <div className="editor-panel">
        <h2>Goals <button className="btn small" type="button" onClick={addGoal}>+ Add</button></h2>
        <div className="goals-list">
          {level.goals.map((g, i) => (
            <div className="goal-row" key={i}>
              <select value={g.target} onChange={(e) => updateGoal(i, { target: e.target.value })}>
                <option value="candy">candy</option>
                <option value="blocker">blocker</option>
                <option value="overlay">overlay</option>
              </select>
              <select value={g.type} onChange={(e) => updateGoal(i, { type: parseInt(e.target.value, 10) })}>
                {Array.from({ length: maxTypeFor[g.target] }, (_, t) => t + 1).map((t) => (
                  <option key={t} value={t}>
                    {g.target === 'candy' ? `color ${t}` : g.target === 'blocker' ? 'crate' : t === 1 ? 'chain' : 'ice'}
                  </option>
                ))}
              </select>
              <input type="number" min="1" style={{ width: 70 }} value={g.count} onChange={(e) => updateGoal(i, { count: parseInt(e.target.value || '0', 10) })} />
              <span className="avail">{g.target === 'candy' ? '' : `grid: ${goalAvailability(level, g.target, g.type)}`}</span>
              <button className="btn remove small" type="button" onClick={() => removeGoal(i)}>✕</button>
            </div>
          ))}
        </div>
      </div>

      <div className="editor-panel">
        <h2>Board</h2>
        <div className="toolbar">
          <div className="tool-group">
            <button type="button" className={`tab ${activeLayer === 'candy' ? 'active' : ''}`} onClick={() => setActiveLayer('candy')}>Candy</button>
            <button type="button" className={`tab ${activeLayer === 'blocker' ? 'active' : ''}`} onClick={() => setActiveLayer('blocker')}>Blocker</button>
            <button type="button" className={`tab ${activeLayer === 'overlay' ? 'active' : ''}`} onClick={() => setActiveLayer('overlay')}>Overlay</button>
          </div>

          {activeLayer === 'candy' && (
            <div className="tool-group">
              <button type="button" className={candyBrush === -1 ? 'selected' : ''} onClick={() => setCandyBrush(-1)}>Hole</button>
              <button type="button" className={candyBrush === 0 ? 'selected' : ''} onClick={() => setCandyBrush(0)}>Empty</button>
              {[1, 2, 3, 4, 5].map((v) => (
                <button key={v} type="button" className={`swatch ${candyBrush === v ? 'selected' : ''}`} style={{ '--sw': CANDY_COLORS[v] }} onClick={() => setCandyBrush(v)}>{v}</button>
              ))}
              <button type="button" className={candyBrush === 6 ? 'selected' : ''} onClick={() => setCandyBrush(6)}>H-Stripe</button>
              <button type="button" className={candyBrush === 7 ? 'selected' : ''} onClick={() => setCandyBrush(7)}>V-Stripe</button>
              <button type="button" className={candyBrush === 8 ? 'selected' : ''} onClick={() => setCandyBrush(8)}>Wrapped</button>
              <button type="button" className={candyBrush === 9 ? 'selected' : ''} onClick={() => setCandyBrush(9)}>ColorBomb</button>
              <button type="button" className={candyBrush === 10 ? 'selected' : ''} onClick={() => setCandyBrush(10)}>Fish</button>
              <label className="boosterColor">Booster color
                <select value={boosterColor} onChange={(e) => setBoosterColor(parseInt(e.target.value, 10))}>
                  {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
            </div>
          )}

          {activeLayer === 'blocker' && (
            <div className="tool-group">
              <button type="button" className={blockerBrush === 0 ? 'selected' : ''} onClick={() => setBlockerBrush(0)}>None</button>
              <button type="button" className={blockerBrush === 1 ? 'selected' : ''} onClick={() => setBlockerBrush(1)}>Crate</button>
              <label>HP
                <select value={blockerHpBrush} onChange={(e) => setBlockerHpBrush(parseInt(e.target.value, 10))}>
                  {[1, 2, 3].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
              <span className="hint">Right-click a crate to cycle HP.</span>
            </div>
          )}

          {activeLayer === 'overlay' && (
            <div className="tool-group">
              <button type="button" className={overlayBrush === 0 ? 'selected' : ''} onClick={() => setOverlayBrush(0)}>None</button>
              <button type="button" className={overlayBrush === 1 ? 'selected' : ''} onClick={() => setOverlayBrush(1)}>Chain</button>
              <button type="button" className={overlayBrush === 2 ? 'selected' : ''} onClick={() => setOverlayBrush(2)}>Ice</button>
              <label>HP
                <select value={overlayHpBrush} onChange={(e) => setOverlayHpBrush(parseInt(e.target.value, 10))}>
                  {[1, 2, 3].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
              <span className="hint">Right-click ice/chain to cycle HP.</span>
            </div>
          )}
        </div>

        <div className="board-wrap">
          <div className="board" style={{ gridTemplateColumns: `repeat(${level.width}, 36px)` }}>
            {Array.from({ length: level.height }, (_, r) =>
              Array.from({ length: level.width }, (_, c) => (
                <Cell key={`${r}-${c}`} level={level} r={r} c={c} onPaint={paintCell} onCycleHp={cycleHp} />
              ))
            )}
          </div>
        </div>
        {toastMsg && <p style={{ color: 'var(--danger-color)', fontSize: 12, marginTop: 8 }}>{toastMsg}</p>}
      </div>

      <div className="editor-panel validation-box">
        <h2>Validation</h2>
        {errors.length === 0 ? (
          <p className="ok">✓ Hợp lệ, sẵn sàng lưu.</p>
        ) : (
          <ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        )}
      </div>

      <div className="editor-footer">
        <button className="btn" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" type="button" onClick={handleSave} disabled={saving || errors.length > 0}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
